import { DialogAwareContentContainer, DialogContent } from "@/components/shared";
import { BREAKPOINTS } from "@/config/breakpoints.config";
import { usePoints } from "@/hooks/points/usePoints";
import { formatTokenAmountForDisplay } from "@/utils";
import { isUndefined } from "lodash-es";
import { useMemo } from "react";

type PointsChapterSummary = {
    episode: number;
    chapter: number;
    points: number;
};

type PointsNewChapterDialogProps = {
    onClose: () => void;
    mockPoints?: PointsChapterSummary[];
    mockCurrentChapter?: number;
    mockCurrentEpisode?: number;
};

export const PointsNewChapterDialog = ({
    mockPoints,
    mockCurrentChapter,
    mockCurrentEpisode,
}: PointsNewChapterDialogProps) => {
    const { points, currentChapter, currentEpisode } = usePoints();
    const effectivePoints = mockPoints ?? points;
    const effectiveCurrentChapter = mockCurrentChapter ?? currentChapter;
    const effectiveCurrentEpisode = mockCurrentEpisode ?? currentEpisode;
    // Parse previous chapter from points (max chapter below current chapter)

    const prevChapterEntry = useMemo(() => {
        if (!effectivePoints || !effectiveCurrentChapter || !effectiveCurrentEpisode) return null;
        // Find all chapters before the current one, considering episode and chapter
        const prevEntries = effectivePoints.filter(
            ({ episode, chapter }) =>
                episode < effectiveCurrentEpisode ||
                (episode === effectiveCurrentEpisode && chapter < effectiveCurrentChapter),
        );
        if (prevEntries.length === 0) return null;
        // Find the entry with the highest episode, then highest chapter
        return prevEntries.reduce((max, entry) => {
            if (entry.episode > max.episode || (entry.episode === max.episode && entry.chapter > max.chapter)) {
                return entry;
            }
            return max;
        }, prevEntries[0]);
    }, [effectivePoints, effectiveCurrentChapter, effectiveCurrentEpisode]);

    const prevChapter = prevChapterEntry?.chapter;
    const prevEpisode = prevChapterEntry?.episode;

    const lastChapterPoints = useMemo(() => {
        if (!effectivePoints || !prevChapterEntry) return 0;
        return effectivePoints
            .filter(
                ({ episode, chapter }) => episode === prevChapterEntry.episode && chapter === prevChapterEntry.chapter,
            )
            .reduce((sum, { points }) => sum + points, 0);
    }, [effectivePoints, prevChapterEntry]);

    // ensure both chapters valid
    if (isUndefined(effectiveCurrentChapter) || isUndefined(prevChapter)) {
        return null;
    }

    // ensure it's exactly the next chapter
    // or it's a new episode starting at chapter 1
    if (effectiveCurrentChapter !== prevChapter + 1 && effectiveCurrentEpisode === prevEpisode) {
        return null;
    }

    // Determine which episode number to display for the "has ended" message
    const displayPrevEpisode = effectiveCurrentEpisode === prevEpisode ? effectiveCurrentEpisode : prevEpisode;

    return (
        <DialogContent style={{ width: BREAKPOINTS.md, minWidth: BREAKPOINTS.xs }} className="border-0">
            <DialogAwareContentContainer isDialog>
                <div className="flex flex-col justify-center items-center py-16 px-16 gap-6">
                    <h1 className="text-3xl text-center pb-0 text-dark">
                        Ep.{displayPrevEpisode} Chapter {prevChapter} has ended.
                    </h1>
                    <span className="text-2xl whitespace-nowrap text-dark/85 border-b-muted border-b-[1px] pb-6">
                        You earned{" "}
                        <span className="font-medium text-primary">
                            {formatTokenAmountForDisplay(lastChapterPoints)} points
                        </span>{" "}
                        in this chapter!
                    </span>
                    <span className="text-xl text-dark/75">
                        <span className="font-medium text-secondary">
                            Ep. {effectiveCurrentEpisode} Chapter {effectiveCurrentChapter}
                        </span>{" "}
                        has begun.
                    </span>
                </div>
            </DialogAwareContentContainer>
        </DialogContent>
    );
};
